import { useEffect, useState } from "react";
import Pagination from "../../common/Pagination";
import { MdDeleteForever } from "react-icons/md";
import { BiSolidEditAlt } from "react-icons/bi";
import Loader from "../../common/Loader";
import axiosInstance from "../../api/axiosInstance.js";
import { notify } from "../../common/Toast/index.js";
import Model from "../../common/Model/index.js";
import Select from "react-select";
import { FaMinus, FaPlus, FaStarOfLife } from "react-icons/fa";
import { getUserMenuPermission } from "../../common/Helper/index.js";

const MenuTable = () => {
  const permission = getUserMenuPermission(12);
  const [isMenuData, setIsMenuData] = useState([]);
  const [users, setUsers] = useState([]);
  const [menus, setMenus] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isModelOpen, setIsModelOpen] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [totalRecords, setTotalRecords] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const pageSize = 10;
  const [errors, setErrors] = useState({});
  const [totalPages, setTotalPages] = useState(1);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [selectedMenuId, setSelectedMenuId] = useState(null);
  const [formData, setFormData] = useState({
    menuId: [],
  });
  const userId = sessionStorage.getItem("userId");

  useEffect(() => {
    fetchMenuData();
    fetchUsers();
    fetchMenus();
  }, [currentPage]);

  const fetchMenus = async () => {
    try {
      const { data } = await axiosInstance.get(
        "UserMenuPermission/GetAllMenus"
      );
      setMenus(data);
    } catch (err) {
      console.error("Error fetching menus:", err);
    }
  };

  const fetchUsers = async () => {
    try {
      const { data } = await axiosInstance.get("User/GetAllUser");
      setUsers(data);
    } catch (err) {
      console.error("Error fetching users:", err);
    }
  };

  const fetchMenuData = async () => {
    try {
      setLoading(true);
      const { data } = await axiosInstance.post(
        `UserMenuPermission/getAllWithMenuName?pageNumber=${currentPage}&pageSize=${pageSize}`,
        {
          pageNumber: currentPage,
          pageSize,
        }
      );
      setIsMenuData(data.data);
      setTotalRecords(data.totalRecords);
      setTotalPages(data.length < pageSize ? currentPage : currentPage + 1);
    } catch (err) {
      console.error("Error fetching data:", err);
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteMenuPermision = (id) => {
    setSelectedMenuId(id);
    setIsDeleteModalOpen(true);
  };

  const handleConfirmDelete = async () => {
    try {
      await axiosInstance.delete(
        `UserMenuPermission/DeleteUser/${selectedMenuId}`
      );
      fetchMenuData();
      notify("Menu deleted successfully!", "error");
    } catch (err) {
      notify("Failed to delete Menu!", "error");
    } finally {
      setIsDeleteModalOpen(false);
      setSelectedMenuId(null);
    }
  };

  const handleEditMenuPermision = async (id) => {
    try {
      const { data } = await axiosInstance.get(
        `UserMenuPermission/getByUserId/${id}`
      );
      if (data.length > 0) {
        setFormData({
          userId: data[0].userId,
          menuId: data.map((item) => ({
            menuId: item.menuId.toString(),
            insert: item.isInsert,
            update: item.isUpdate,
            delete: item.isDelete,
            view: item.isView,
          })),
        });

        setIsEditing(true);
        setIsModelOpen(true);
      }
    } catch (err) {
      console.error("Error fetching data by ID:", err);
    }
  };

  const handleAddMenuPermision = () => {
    if (!permission.isInsert) {
      notify("You don't have permission to add menu.", "error");
      return;
    }
    setFormData({
      menuId: [
        {
          userId: "",
          menuId: "",
          insert: false,
          update: false,
          delete: false,
          view: false,
        },
      ],
    });
    setIsEditing(false);
    setIsModelOpen(true);
  };
  const handleAddRow = () => {
    setFormData({
      ...formData,
      menuId: [
        ...formData.menuId,
        {
          userId: "",
          menuId: "",
          insert: false,
          update: false,
          delete: false,
          view: false,
        },
      ],
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!isEditing && !permission.isInsert) return;
    if (isEditing && !permission.isUpdate) return;
    if (!formData.userId) {
      notify("Please select user", "error");
      return;
    }

    const payload = formData.menuId.map((menu) => ({
      UserId: Number(formData.userId),
      MenuId: Number(menu.menuId),
      IsInsert: menu.insert,
      IsUpdate: menu.update,
      IsDelete: menu.delete,
      IsView: menu.view,
      Created_By: userId,
      Modified_By: userId,
    }));

    try {
      await axiosInstance.post(`UserMenuPermission/saveOrUpdate`, payload);

      notify(
        isEditing ? "Menu Permission Updated" : "Menu Permission Saved",
        "success"
      );
      fetchMenuData();
      setIsModelOpen(false);
    } catch (err) {
      console.error("Error Saving/Updating:", err);
      notify("Something went wrong!", "error");
    }
  };

  const handleDeleteRow = async (menu, index) => {
    try {
      const updated = [...formData.menuId];

      if (menu.menuId) {
        await axiosInstance.delete(
          `UserMenuPermission/delete/${formData.userId}/${menu.menuId}`
        );
        notify("Row Deleted Successfully", "success");
      }

      updated.splice(index, 1);
      setFormData({ ...formData, menuId: updated });
    } catch (err) {
      notify("Failed to delete row!", "error");
    }
  };

  const Options = [
    ...users.map((per) => ({
      value: per.sale_Person_Id?.toString() || "",
      label: per.Sale_Person_Name || "Unnamed User",
    })),
  ];

  const menuOptions = menus.map((menu) => ({
    value: String(menu.menuIDs),
    label: menu.menu_Name || "Unnamed Menu",
    isDisabled: formData.menuId.some(
      (item) => String(item.menuId) === String(menu.menuIDs)
    ),
  }));

  return (
    <>
      <div className="rounded-md bg-white shadow-md border px-5 pt-6 pb-2.5">
        <div className="flex flex-wrap justify-between items-center gap-4 mb-4">
          <h2 className="text-lg font-bold">Total Menu Permission</h2>
          {permission.isInsert && (
            <button
              className="bg-primary rounded px-4 text-white py-2 w-full sm:w-auto"
              onClick={() => handleAddMenuPermision()}
            >
              + Add Menu Permission
            </button>
          )}
        </div>

        {loading ? (
          <Loader />
        ) : permission.isView ? (
          <div className="overflow-x-auto rounded-lg">
            <table className="w-full min-w-[700px] text-sm text-left border border-gray ">
              <thead className="bg-primary text-white tracking-widest text-center font-light">
                <tr>
                  <th className="border border-gray px-4 py-3">User Name</th>
                  <th className="border border-gray px-4 py-3">Menu Names</th>
                  <th
                    className={`${
                      !permission.isUpdate && !permission.isDelete
                        ? "hidden"
                        : ""
                    } border border-gray px-4 py-3 whitespace-nowrap`}
                  >
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody>
                {isMenuData?.length > 0 ? (
                  isMenuData?.map((menu) => (
                    <tr className="border-b hover:bg-gray-50 transition cursor-pointer text-center">
                      <td className="border border-gray-100 px-4 py-2">
                        {users?.find(
                          (user) =>
                            Number(user.sale_Person_Id) === Number(menu.userId)
                        )?.Sale_Person_Name || "Unknown"}
                      </td>
                      <td className="border border-gray-100 px-4 py-2">
                        {menu.menuNames}
                      </td>

                      <td
                        className={`border border-gray-100 py-2 text-center ${
                          !permission.isUpdate && !permission.isDelete
                            ? "hidden"
                            : ""
                        }`}
                      >
                        {permission.isUpdate && (
                          <button
                            className="p-1 text-primary hover:bg-gray-100 rounded-md"
                            onClick={() => {
                              handleEditMenuPermision(menu.userId);
                            }}
                          >
                            <BiSolidEditAlt className="w-8 h-8 bg-[#e3f2fd] hover:bg-blue-200 p-1 rounded-md outline-none focus:ring-2 focus:ring-blue-500" />
                          </button>
                        )}
                        {permission.isDelete && (
                          <button
                            className="p-1 text-danger hover:bg-gray-100 rounded-md"
                            onClick={() =>
                              handleDeleteMenuPermision(menu.userId)
                            }
                          >
                            <MdDeleteForever className="w-8 h-8 bg-red-100 p-1 rounded-md hover:bg-red-200 outline-none focus:ring-2 focus:ring-red-500" />
                          </button>
                        )}
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="6" className="text-center py-4">
                      No records found
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="text-center text-red-500 text-lg py-10">
            You don't have permission to view this page.
          </div>
        )}

        <div className="flex justify-between items-center mt-4">
          <p className="text-sm text-gray-6">
            Showing {isMenuData.length + (currentPage - 1) * 10} of{" "}
            {totalRecords} Records
          </p>
          <Pagination
            currentPage={currentPage}
            totalPages={totalPages}
            onPageChange={setCurrentPage}
          />
        </div>
      </div>
      <Model
        isOpen={isModelOpen}
        onClose={() => setIsModelOpen(false)}
        name={isEditing ? "Edit Menu Permission" : "Add Menu Permission"}
      >
        <form onSubmit={handleSubmit} className=" space-y-4">
          <div>
            <label className="flex items-center text-sm font-medium mb-1">
              User
              <FaStarOfLife className="text-danger ml-1 w-2 h-2" />
            </label>
            {isEditing ? (
              <input
                type="text"
                value={
                  users.find(
                    (user) =>
                      Number(user.sale_Person_Id) === Number(formData.userId)
                  )?.Sale_Person_Name || "Unknown"
                }
                readOnly
                className="mt-1 block w-full rounded border border-gray-3 bg-gray-100 px-4 py-2 shadow-sm"
              />
            ) : (
              <Select
                options={Options}
                value={
                  formData.userId
                    ? Options.find(
                        (option) =>
                          String(option.value) === String(formData.userId)
                      ) || null
                    : null
                }
                isClearable
                isSearchable
                onChange={(selectedOption) =>
                  setFormData((prev) => ({
                    ...prev,
                    userId: selectedOption ? selectedOption.value : "",
                  }))
                }
                menuPortalTarget={document.body}
                styles={{
                  control: (provided) => ({
                    ...provided,
                    boxShadow: "none",
                    "&:hover": { borderColor: "gray" },
                  }),
                  menu: (provided) => ({
                    ...provided,
                    maxHeight: "300px",
                    overflowY: "auto",
                  }),
                  menuPortal: (base) => ({
                    ...base,
                    zIndex: 9999,
                  }),
                  menuList: (provided) => ({
                    ...provided,
                    maxHeight: "300px",
                    overflowY: "auto",
                  }),
                }}
              />
            )}
            {errors.userId && (
              <p className="text-danger text-xs mt-1">{errors.userId}</p>
            )}
          </div>
          <div className="overflow-x-auto rounded-lg">
            <table className="table-auto text-sm w-full border-collapse border border-gray-3">
              <thead className="bg-stroke">
                <tr>
                  <th className="border border-gray px-2 py-2 text-left">
                    Menu
                  </th>
                  <th className="border border-gray px-2 py-2 text-center">
                    Insert
                  </th>
                  <th className="border border-gray px-2 py-2 text-center">
                    Update
                  </th>
                  <th className="border border-gray px-2 py-2 text-center">
                    Delete
                  </th>
                  <th className="border border-gray px-2 py-2 text-center">
                    View
                  </th>
                  <th className="border border-gray px-2 py-2 text-center">
                    Action
                  </th>
                </tr>
              </thead>
              <tbody>
                {formData.menuId?.map((menu, index) => (
                  <tr key={index} className="hover:bg-gray-50">
                    <td className="border border-gray px-2 py-2">
                      <Select
                        options={menuOptions}
                        value={menuOptions.find(
                          (opt) => String(opt.value) === String(menu.menuId)
                        )}
                        isClearable
                        isSearchable
                        onChange={(selected) => {
                          const updated = [...formData.menuId];
                          updated[index].menuId = selected
                            ? selected.value
                            : "";
                          setFormData({ ...formData, menuId: updated });
                        }}
                        menuPortalTarget={document.body}
                        menuPosition="fixed"
                        styles={{
                          control: (provided) => ({
                            ...provided,
                            boxShadow: "none",
                            "&:hover": { borderColor: "gray" },
                          }),
                          menu: (provided) => ({
                            ...provided,
                            maxHeight: "300px",
                            overflowY: "auto",
                          }),
                          menuPortal: (base) => ({
                            ...base,
                            zIndex: 9999,
                          }),
                          menuList: (provided) => ({
                            ...provided,
                            maxHeight: "300px",
                            overflowY: "auto",
                          }),
                        }}
                      />
                    </td>
                    {["insert", "update", "delete", "view"].map((perm) => (
                      <td
                        className="border border-gray px-2 py-2 text-center"
                        key={perm}
                      >
                        <input
                          type="checkbox"
                          checked={menu[perm]}
                          onChange={() => {
                            const updated = [...formData.menuId];
                            updated[index][perm] = !menu[perm];
                            setFormData({ ...formData, menuId: updated });
                          }}
                        />
                      </td>
                    ))}
                    <td className="border border-gray px-2 py-2 text-center">
                      <button
                        type="button"
                        className="text-danger bg-red-100 p-1 rounded-md hover:bg-red-200"
                        onClick={() => handleDeleteRow(menu, index)}
                      >
                        <FaMinus />
                      </button>
                      <button
                        type="button"
                        onClick={() => handleAddRow()}
                        className="text-primary bg-blue-100 p-1 rounded-md hover:bg-blue-200 ml-2"
                      >
                        <FaPlus />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="flex justify-end gap-4">
            <button
              type="button"
              className="px-4 py-2 bg-gray rounded-md shadow-sm"
              onClick={() => setIsModelOpen(false)}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="px-6 py-2 bg-primary text-white rounded-md shadow-md"
            >
              {isEditing ? "Update" : "Save"}
            </button>
          </div>
        </form>
      </Model>

      <Model
        isOpen={isDeleteModalOpen}
        onClose={() => setIsDeleteModalOpen(false)}
      >
        <div className="p-4 text-center">
          <p className="text-lg font-semibold mb-4">
            Are you sure you want to delete this site?
          </p>
          <div className="flex justify-center gap-4">
            <button
              className="bg-gray-5 px-4 py-2 rounded-md"
              onClick={() => setIsDeleteModalOpen(false)}
            >
              Cancel
            </button>
            <button
              className="bg-danger text-white px-4 py-2 rounded-md"
              onClick={handleConfirmDelete}
            >
              Delete
            </button>
          </div>
        </div>
      </Model>
    </>
  );
};

export default MenuTable;
